import { useState } from 'react';
import { ArrowUpCircle, Loader2, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { ReviewSessionSummary, DiffSection } from '@/types/api';

interface EscalateDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly session: ReviewSessionSummary;
  readonly selectedSection: DiffSection | null;
  readonly onConfirm: (notes: string) => void;
  readonly isLoading: boolean;
}

const MIN_LENGTH = 20;

export function EscalateDialog({
  open,
  onOpenChange,
  selectedSection,
  onConfirm,
  isLoading,
}: EscalateDialogProps) {
  const [notes, setNotes] = useState('');
  const isValid = notes.trim().length >= MIN_LENGTH;

  const handleSubmit = () => {
    if (isValid) {
      onConfirm(notes.trim());
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpCircle className="h-5 w-5 text-amber-400" />
            Escalate to CCO
          </DialogTitle>
          <DialogDescription>
            This will route the review to the Chief Compliance Officer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {selectedSection && (
            <div className="flex items-start gap-3 rounded-md border border-blue-500/30 bg-blue-500/5 p-3">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
              <p className="text-sm text-blue-300">
                Flagged section:{' '}
                <span className="font-medium">{selectedSection.sectionTitle}</span>
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="escalate-notes">Reason for escalation</Label>
            <Textarea
              id="escalate-notes"
              placeholder="Describe why this requires CCO review..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Minimum {MIN_LENGTH} characters
              </p>
              <p className="tabular-nums text-xs text-muted-foreground">
                {notes.trim().length}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            className="bg-amber-500 text-white hover:bg-amber-500/90"
            onClick={handleSubmit}
            disabled={!isValid || isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Escalate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
